import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Phone, MapPin, Mail, Instagram, Facebook } from "lucide-react";
import { useLang } from "../lib/LanguageContext";
import { api } from "../lib/api";
import Newsletter from "./Newsletter";

export default function Footer() {
    const { t, lang } = useLang();
    const [site, setSite] = useState(null);

    useEffect(() => {
        api.get("/site/content").then((r) => setSite(r.data)).catch(() => {});
    }, []);

    const phone = site?.phone || "0682116731";
    const email = site?.email || "info@dearadhime.al";
    const address = lang === "al" ? site?.address_al : site?.address_en;

    return (
        <footer
            data-testid="site-footer"
            className="bg-[#0F3A59] text-white pt-20 pb-10 mt-20"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-4 gap-12">
                <div className="md:col-span-2">
                    <h3 className="font-serif text-3xl mb-3">
                        Kompleksi Dea
                        <span className="block text-xs tracking-[0.3em] uppercase mt-1 font-sans font-light opacity-70">
                            Radhimë
                        </span>
                    </h3>
                    <p className="text-white/70 font-light max-w-md leading-relaxed">
                        {t("footer_tagline")}
                    </p>
                    <div className="mt-6">
                        <div className="text-xs uppercase tracking-[0.2em] text-white/60 mb-2">
                            {lang === "al" ? "Ofertat speciale" : "Special offers"}
                        </div>
                        <Newsletter variant="footer" />
                    </div>
                    <div className="flex gap-3 mt-6">
                        {site?.instagram && (
                            <a
                                href={site.instagram}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="footer-instagram"
                                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#8134AF] text-white text-sm font-medium hover:scale-105 transition-transform shadow-lg"
                            >
                                <Instagram size={18} strokeWidth={2} />
                                Instagram
                            </a>
                        )}
                        {site?.facebook && (
                            <a
                                href={site.facebook}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="footer-facebook"
                                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1877F2] text-white text-sm font-medium hover:scale-105 transition-transform shadow-lg"
                            >
                                <Facebook size={18} strokeWidth={2} />
                                Facebook
                            </a>
                        )}
                    </div>
                </div>

                <div>
                    <h4 className="font-serif text-lg mb-4">{t("nav_contact")}</h4>
                    <ul className="space-y-3 text-sm font-light">
                        <li className="flex items-start gap-2 text-white/80">
                            <MapPin size={16} strokeWidth={1.5} className="mt-0.5 shrink-0" />
                            <span>{address || "Radhimë, Vlorë"}</span>
                        </li>
                        <li className="flex items-center gap-2 text-white/80">
                            <Phone size={16} strokeWidth={1.5} />
                            <a href={`tel:${phone}`} className="hover:text-white">
                                {phone}
                            </a>
                        </li>
                        <li className="flex items-center gap-2 text-white/80">
                            <Mail size={16} strokeWidth={1.5} />
                            <a href={`mailto:${email}`} className="hover:text-white">
                                {email}
                            </a>
                        </li>
                    </ul>
                </div>

                <div>
                    <h4 className="font-serif text-lg mb-4">{t("nav_home")}</h4>
                    <ul className="space-y-2.5 text-sm font-light">
                        <li>
                            <Link to="/rooms" className="text-white/80 hover:text-white">
                                {t("nav_rooms")}
                            </Link>
                        </li>
                        <li>
                            <Link to="/facilities" className="text-white/80 hover:text-white">
                                {t("nav_facilities")}
                            </Link>
                        </li>
                        <li>
                            <Link to="/gallery" className="text-white/80 hover:text-white">
                                {t("nav_gallery")}
                            </Link>
                        </li>
                        <li>
                            <Link to="/blog" className="text-white/80 hover:text-white">
                                {t("nav_blog")}
                            </Link>
                        </li>
                        <li>
                            <Link to="/faq" className="text-white/80 hover:text-white">
                                {t("nav_faq")}
                            </Link>
                        </li>
                        <li>
                            <Link to="/contact" className="text-white/80 hover:text-white">
                                {t("nav_contact")}
                            </Link>
                        </li>
                    </ul>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-6 md:px-12 mt-16 pt-8 border-t border-white/10 flex flex-col sm:flex-row justify-between text-xs text-white/50 font-light gap-2">
                <span>
                    © {new Date().getFullYear()} Kompleksi Dea Radhimë. {t("footer_rights")}.
                </span>
                <Link
                    to="/admin/login"
                    data-testid="footer-admin-link"
                    className="hover:text-white/70 transition"
                >
                    {t("nav_admin")}
                </Link>
            </div>
        </footer>
    );
}
